/**
 * MemoryRef — opaque typed reference for resumable agent context.
 *
 * The ref itself is a lightweight pointer; the actual memory content
 * is managed by the MemoryStore or held server-side by the provider.
 */
export interface MemoryRef {
  /** Opaque identifier (e.g. session UUID, response ID, agent ID). */
  id: string;
  /** Provider that generated this ref (e.g. "claude-agent-sdk", "openai-responses"). */
  provider: string;
  /** Compatibility tag for format validation across versions. */
  compat: string;
  /** ISO 8601 timestamp of ref creation. */
  created_at: string;
  /** ID of the run that produced this ref, for chain tracking. */
  parent_run_id?: string;
}

/**
 * Pluggable store for memory content that backs MemoryRef pointers.
 *
 * Adapters that use server-side state (OpenAI, Gemini) may not need
 * the store — the ref ID alone is sufficient for resume. Adapters
 * with local state (Claude transcript, Gemini chat history) use the
 * store to persist and retrieve content.
 */
export interface MemoryStore {
  save(ref: MemoryRef, content: unknown): Promise<void>;
  load(ref: MemoryRef): Promise<unknown | null>;
  delete(ref: MemoryRef): Promise<void>;
}

/**
 * In-memory store for testing. State is lost when the process exits.
 */
export class InMemoryStore implements MemoryStore {
  private data = new Map<string, unknown>();

  async save(ref: MemoryRef, content: unknown): Promise<void> {
    this.data.set(ref.id, content);
  }

  async load(ref: MemoryRef): Promise<unknown | null> {
    return this.data.get(ref.id) ?? null;
  }

  async delete(ref: MemoryRef): Promise<void> {
    this.data.delete(ref.id);
  }
}
